import { useSessionStore } from "@auth/sessionStore";
import { ClosePaneButton } from "@components/ClosePaneButton";
import { EmptyNote } from "@scribenote/brian-design-system";
import { useResetOnChange } from "@utils/use-reset-on-change";
import { useViewPdfExport } from "@views/useViewPdfExport";
import { ViewBody } from "@views/ViewBody";
import { ViewCard } from "@views/ViewCard";
import { ViewDeleteButton } from "@views/ViewDeleteButton";
import { ViewExportButton } from "@views/ViewExportButton";
import { viewCaption, withSubjectFor } from "@views/viewIdentity";
import type { View } from "@views/viewsClient";
import { ViewSignoffs } from "@views/ViewSignoffs";
import { useViewsList } from "@views/viewsQueries";
import { ViewTaskBell } from "@views/ViewTaskBell";
import { useState } from "react";

/**
 * The list → detail → delete flow for SavedViews, shared by the admin Views
 * panel (clinic-wide) and the subject detail pane (filtered to one subject).
 * Selection is local; views render via the shared a2ui renderer in read-only
 * mode. The list rows already carry the full tree, so the detail needs no
 * extra fetch.
 */
export const ViewsBrowser = ({
  subject,
  emptyLabel,
}: {
  subject: string | null;
  emptyLabel: string;
}) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: views } = useViewsList(sessionKey, subject);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useResetOnChange(subject, () => setSelectedId(null));

  if (views.length === 0) return <EmptyNote>{emptyLabel}</EmptyNote>;

  // Decided per view, not per list (BE-2395): the pane is only redundant about
  // the patient when it IS that patient. `subject` here can be a client whose
  // pane lists two pets' notes, or a room that hosts someone new every few
  // hours - both need the name, same as the clinic-wide panel.
  const selected = views.find((v) => v.id === selectedId);
  if (selected)
    return (
      <ViewDetail
        view={selected}
        withSubject={withSubjectFor(selected, subject)}
        onClose={() => setSelectedId(null)}
      />
    );

  return (
    <div className="flex flex-col gap-3">
      {views.map((v) => (
        <ViewCard
          key={v.id}
          view={v}
          withSubject={withSubjectFor(v, subject)}
          onClick={() => setSelectedId(v.id)}
          badge={<ViewTaskBell view={v} compact />}
        />
      ))}
    </div>
  );
};

const ViewDetail = ({
  view,
  withSubject,
  onClose,
}: {
  view: View;
  withSubject: boolean;
  onClose: () => void;
}) => {
  const { ref, exportPdf, exporting } = useViewPdfExport(view);
  const caption = viewCaption(view, { withSubject });

  return (
    <article className="flex h-full flex-col gap-3">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg font-bold text-ink">
            {view.name}
          </h2>
          {caption && (
            <p className="truncate text-sm text-ink-body">{caption}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <ViewTaskBell view={view} />
          <ViewExportButton
            viewId={view.id}
            onExport={exportPdf}
            exporting={exporting}
          />
          <ViewDeleteButton view={view} onDeleted={onClose} />
          <ClosePaneButton onClick={onClose} label="Close view" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <div ref={ref}>
          <ViewBody view={view} />
        </div>
        {view.status === "signed" && <ViewSignoffs view={view} />}
      </div>
    </article>
  );
};
